import Page from "@/containers/Page";
import { Col, Div, Row } from "@/styles/layout";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePageInfo } from "@/hooks/usePageInfo";
import Card from "@/containers/Card";
import { toast } from "react-hot-toast";
import { useMessaging } from "@/hooks/useMessaging";
import {
  Messaging,
  MessagingUpdatePayloadKeys,
} from "@/types/models/Messaging";
import { updateMessaging } from "@/services/messaging.service";
import { Body } from "@/styles/typography";
import { useAccount } from "@/hooks/useAccount";
import Loader from "@/components/Loader";
import FormPreview from "@/containers/FormPreview";
import Editor, { EditorData, MergeTags } from "@/components/Editor";
import { Tags } from "@/lib/mergeTags";

const EDITOR_WIDTH = 450;

interface MessagingConfig extends Omit<ItemProps, "onSelect" | "value"> {
  type: "all" | "roundups" | "classic";
  maxLength?: number;
  mergeTags: MergeTags[];
}

const EMAILS: MessagingConfig[] = [
  {
    title: "Round-Up Plan Subscription",
    description:
      "Email sent when a donor subscribe to a round-up donation plan.",
    field: "emailRoundupRegistration",
    type: "roundups",
    mergeTags: [Tags.firstName, Tags.lastName, Tags.email],
  },
  {
    title: "Round-Up Donation",
    description:
      "Email sent when the round-ups of a donor are transferred to your cause.",
    field: "emailRoundupDonation",
    type: "roundups",
    mergeTags: [Tags.firstName, Tags.lastName, Tags.email, Tags.amount],
  },
  {
    title: "One-Time Donation",
    description: "Email sent when a donor make a one-time donations.",
    field: "emailOnetimeDonation",
    type: "classic",
    mergeTags: [Tags.firstName, Tags.lastName, Tags.email, Tags.amount],
  },
  {
    title: "Recurring Plan Subscription",
    description:
      "Email sent when a donor subscribe to a recurring donation plan.",
    field: "emailMonthlyRegistration",
    type: "classic",
    mergeTags: [Tags.firstName, Tags.lastName, Tags.email, Tags.amount],
  },
  {
    title: "Recurring Donation",
    description: "Email sent when a recurring donation is processed.",
    field: "emailMonthlyDonation",
    type: "classic",
    mergeTags: [Tags.firstName, Tags.lastName, Tags.email, Tags.amount],
  },
];

const FORMS: MessagingConfig[] = [
  {
    title: "Ask Heading",
    description: "The heading of the left side panel of the donation form.",
    field: "formAskHeading",
    type: "all",
    maxLength: 50,
    mergeTags: [],
  },
  {
    title: "Ask Content",
    description: "The content of the left side panel of the donation form.",
    field: "formAskContent",
    type: "all",
    maxLength: 500,
    mergeTags: [],
  },
  {
    title: "Round-up Subscription",
    description:
      "The message is displayed when a donor subscribes to a round-up donation plan.",
    field: "formRoundupThankYou",
    type: "roundups",
    maxLength: 300,
    mergeTags: [Tags.firstName, Tags.lastName, Tags.email],
  },
  {
    title: "One-Time Donation",
    description: "The message is displayed when a donor make a donation.",
    field: "formOnetimeThankYou",
    type: "classic",
    maxLength: 300,
    mergeTags: [Tags.firstName, Tags.lastName, Tags.email, Tags.amount],
  },
  {
    title: "Recurring Donation",
    description:
      "The message is displayed when a donor subscribes to a recurring donation plan.",
    field: "formMonthlyThankYou",
    type: "classic",
    maxLength: 300,
    mergeTags: [Tags.firstName, Tags.lastName, Tags.email, Tags.amount],
  },
];

const RECEIPT: MessagingConfig[] = [
  {
    title: "Tax receipt message",
    description: "The message written in the tax receipt sent to your donor.",
    field: "receiptMessage",
    type: "all",
    maxLength: 300,
    mergeTags: [Tags.firstName, Tags.lastName, Tags.amount],
  },
];

export default function FormMessaging() {
  const { account, isLoading } = useAccount();
  const { messaging, refresh, isLoading: messagingLoading } = useMessaging();
  const [editorData, setEditorData] = useState<EditorData | undefined>();

  const { roundupEnabled, classicEnabled } = useMemo(() => {
    if (account?.settings) {
      return {
        roundupEnabled: account.settings.roundupEnabled,
        classicEnabled: account.settings.classicEnabled,
      };
    }
    return {
      roundupEnabled: false,
      classicEnabled: false,
    };
  }, [account]);

  const handleEditor = useCallback(
    (data: EditorData | undefined) => {
      if (data && messaging) {
        setEditorData(data);
      }
    },
    [messaging]
  );

  const handleEditorSave = async (field: string, value: string) => {
    try {
      if (messaging?.id) {
        const input: Partial<Omit<Messaging, "createdAt" | "updatedAt">> = {
          id: messaging.id,
        };
        input[field as MessagingUpdatePayloadKeys] = value;

        await updateMessaging(input);
        refresh();
        toast.success("Nicely worded!");
      }
    } catch (e) {
      toast.error("Hmm, we couldn't update your message.");
    }
  };

  const routeInfo = usePageInfo();

  const filterField = useCallback(
    ({ type }: MessagingConfig) => {
      if (type === "all") {
        return true;
      } else if (type === "roundups" && roundupEnabled) {
        return true;
      } else if (type === "classic" && classicEnabled) {
        return true;
      }
      return false;
    },
    [roundupEnabled, classicEnabled]
  );

  return (
    <Page
      title={`${routeInfo.name} | GoGive`}
      header={routeInfo.name!}
      Icon={routeInfo.icon!}
    >
      {messagingLoading && (
        <Row justifyContent={"center"}>
          <Loader size={32} />
        </Row>
      )}
      {messaging && (
        <Div
          height="100%"
          display="grid"
          gridTemplateColumns={"repeat(auto-fit, minmax(300px,1fr)) "}
          gridGap="24px"
        >
          <Card
            header={`Form`}
            description="Personalise the messaging on your donation form."
          >
            {FORMS.filter(filterField).map((d) => (
              <Item
                key={d.field}
                data={{ ...d, value: messaging[d.field] }}
                onSelect={handleEditor}
              />
            ))}
          </Card>

          <Card
            header={`Emails`}
            description="Add a few kind words to the emails sent to your donors."
          >
            {EMAILS.filter(filterField).map((d) => (
              <Item
                key={d.field}
                data={{ ...d, value: messaging[d.field] }}
                onSelect={handleEditor}
              />
            ))}
          </Card>
          <Card header={`Receipt`} description="Personalise your tax receipt.">
            {RECEIPT.filter(filterField).map((d) => (
              <Item
                key={d.field}
                data={{ ...d, value: messaging[d.field] }}
                onSelect={handleEditor}
              />
            ))}
          </Card>
        </Div>
      )}

      <Editor
        data={editorData}
        onSave={handleEditorSave}
        onClose={() => setEditorData(undefined)}
      />
    </Page>
  );
}

interface ItemProps {
  title: string;
  description: string;
  field: MessagingUpdatePayloadKeys;
  value: string;
  onSelect: (data: EditorData | undefined) => void;
}

const Item = ({
  data,
  onSelect,
}: {
  data: EditorData;

  onSelect: (data: EditorData | undefined) => void;
}) => {
  return (
    <Row
      justifyContent={"space-between"}
      alignItems={"center"}
      boxShadow={"m"}
      bg="primary.bgBase"
      p="16px"
      borderRadius={"8px"}
      mb="24px"
      onClick={() => {
        onSelect({ ...data });
      }}
    >
      <Col>
        <Body fontSize="14px" fontWeight="500">
          {data.title}
        </Body>
        <Body fontSize="12px" fontWeight="300" color="gray.10">
          {data.description}
        </Body>
      </Col>
      <Col alignItems="center">
        <Body
          fontSize="12px"
          fontWeight="300"
          style={{ textDecoration: "underline", cursor: "pointer" }}
          color="primary.textLow"
        >
          Edit
        </Body>
      </Col>
    </Row>
  );
};
